"use client";

import { useState, useMemo } from "react";
import { Search, X, SlidersHorizontal, ChevronDown } from "lucide-react";
import { cn } from "@/lib/format";
import { PackageCardView } from "../card/PackageCardView";

// ─── Types ────────────────────────────────────────────────────────────────────

export type PackageItem = {
  id: number;
  title: string;
  slug: string;
  href: string;
  summary: string;
  duration: string;
  price: string | null;
  image: string | null;
  imageAlt: string;
  destination: string | null;
  destinationSlug: string | null;
  rating: number | null;
  reviewCount: number;
  isBestSeller: boolean;
  isFeatured: boolean;
  ribbon: string | null;
  minDays: number;
  maxDays: number;
  priceRaw: number;
};

type Destination = { slug: string; name: string };

type Props = {
  packages: PackageItem[];
  destinations: Destination[];
};

// ─── Constants ────────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "price_asc", label: "Price: Low → High" },
  { value: "price_desc", label: "Price: High → Low" },
  { value: "duration_asc", label: "Duration: Shortest" },
  { value: "duration_desc", label: "Duration: Longest" },
  { value: "rating", label: "Top Rated" },
];

const DURATION_RANGES = [
  { label: "1–7 days", min: 1, max: 7 },
  { label: "8–14 days", min: 8, max: 14 },
  { label: "15–21 days", min: 15, max: 21 },
  { label: "22+ days", min: 22, max: Infinity },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function FilterSection({
  label,
  children,
  defaultOpen = true,
}: {
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-line pb-5 last:border-0 last:pb-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between py-1 text-left"
      >
        <span className="text-[11px] font-bold uppercase tracking-widest text-muted-soft">
          {label}
        </span>
        <ChevronDown
          size={14}
          className={cn(
            "text-muted-soft transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

function CheckItem({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-[13px] transition-colors border",
        checked
          ? "bg-primary/8 border-primary/25 text-primary font-medium"
          : "border-transparent text-muted hover:bg-bg",
      )}
    >
      <span
        className={cn(
          "h-3.5 w-3.5 shrink-0 rounded-sm border transition-colors",
          checked ? "border-primary bg-primary" : "border-line bg-white",
        )}
      />
      <span className="line-clamp-1">{label}</span>
    </button>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const PRICE_RANGES = [
  { label: "Under $500", min: 0, max: 500 },
  { label: "$500 – $1,000", min: 500, max: 1000 },
  { label: "$1,000 – $2,000", min: 1000, max: 2000 },
  { label: "$2,000+", min: 2000, max: Infinity },
];

export function SearchClient({ packages, destinations }: Props) {
  const [search, setSearch] = useState("");
  const [activeDestinations, setActiveDestinations] = useState<string[]>([]);
  const [activeDuration, setActiveDuration] = useState<number | null>(null);
  const [activePrice, setActivePrice] = useState<number | null>(null);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [bestSellerOnly, setBestSellerOnly] = useState(false);
  const [sort, setSort] = useState("default");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const hasFilters =
    !!search.trim() ||
    activeDestinations.length > 0 ||
    activeDuration !== null ||
    activePrice !== null ||
    featuredOnly ||
    bestSellerOnly;

  function toggleDestination(slug: string) {
    setActiveDestinations((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = packages.filter((p) => {
      if (
        q &&
        !p.title.toLowerCase().includes(q) &&
        !p.destination?.toLowerCase().includes(q)
      )
        return false;
      if (
        activeDestinations.length &&
        !activeDestinations.includes(p.destinationSlug ?? "")
      )
        return false;
      if (activeDuration !== null) {
        const range = DURATION_RANGES[activeDuration];
        if (p.minDays < range.min || p.minDays > range.max) return false;
      }
      if (activePrice !== null) {
        const range = PRICE_RANGES[activePrice];
        if (p.priceRaw < range.min || p.priceRaw > range.max) return false;
      }
      if (featuredOnly && !p.isFeatured) return false;
      if (bestSellerOnly && !p.isBestSeller) return false;
      return true;
    });

    switch (sort) {
      case "price_asc":
        result = [...result].sort((a, b) => a.priceRaw - b.priceRaw);
        break;
      case "price_desc":
        result = [...result].sort((a, b) => b.priceRaw - a.priceRaw);
        break;
      case "duration_asc":
        result = [...result].sort((a, b) => a.minDays - b.minDays);
        break;
      case "duration_desc":
        result = [...result].sort((a, b) => b.minDays - a.minDays);
        break;
      case "rating":
        result = [...result].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
        break;
    }

    return result;
  }, [
    packages,
    search,
    activeDestinations,
    activeDuration,
    activePrice,
    featuredOnly,
    bestSellerOnly,
    sort,
  ]);

  function clearAll() {
    setSearch("");
    setActiveDestinations([]);
    setActiveDuration(null);
    setActivePrice(null);
    setFeaturedOnly(false);
    setBestSellerOnly(false);
    setSort("default");
  }

  const activeFilterCount = [
    search.trim(),
    ...activeDestinations,
    activeDuration !== null ? "dur" : null,
    activePrice !== null ? "price" : null,
    featuredOnly ? "feat" : null,
    bestSellerOnly ? "best" : null,
  ].filter(Boolean).length;

  // ── Sidebar ───────────────────────────────────────────────────────────────
  const sidebar = (
    <aside className="space-y-5">
      {/* Search */}
      <div className="relative">
        <Search
          size={14}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-soft"
          aria-hidden
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search packages…"
          aria-label="Search packages"
          className="h-10 w-full rounded-xl border border-line bg-bg pl-9 pr-9 text-[13px] text-headings placeholder:text-muted-soft transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-soft hover:text-headings"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Destination */}
      {destinations.length > 0 && (
        <FilterSection label="Destination">
          <div className="space-y-1">
            {destinations.map((d) => (
              <CheckItem
                key={d.slug}
                label={d.name}
                checked={activeDestinations.includes(d.slug)}
                onToggle={() => toggleDestination(d.slug)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Duration */}
      <FilterSection label="Duration">
        <div className="space-y-1">
          {DURATION_RANGES.map((r, i) => (
            <CheckItem
              key={r.label}
              label={r.label}
              checked={activeDuration === i}
              onToggle={() =>
                setActiveDuration(activeDuration === i ? null : i)
              }
            />
          ))}
        </div>
      </FilterSection>

      {/* Price */}
      <FilterSection label="Price">
        <div className="space-y-1">
          {PRICE_RANGES.map((r, i) => (
            <CheckItem
              key={r.label}
              label={r.label}
              checked={activePrice === i}
              onToggle={() => setActivePrice(activePrice === i ? null : i)}
            />
          ))}
        </div>
      </FilterSection>

      {/* Trip Type */}
      <FilterSection label="Trip type" defaultOpen={false}>
        <div className="space-y-1">
          <CheckItem
            label="Featured trips"
            checked={featuredOnly}
            onToggle={() => setFeaturedOnly((v) => !v)}
          />
          <CheckItem
            label="Best sellers"
            checked={bestSellerOnly}
            onToggle={() => setBestSellerOnly((v) => !v)}
          />
        </div>
      </FilterSection>

      {/* Clear */}
      {hasFilters && (
        <button
          type="button"
          onClick={clearAll}
          className="w-full rounded-xl border border-line bg-white py-2.5 text-sm font-semibold text-primary shadow-sm hover:bg-primary/5 transition-colors"
        >
          Clear all filters
        </button>
      )}
    </aside>
  );

  return (
    <div>
      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">
          <span className="font-semibold text-headings">{filtered.length}</span>{" "}
          of{" "}
          <span className="font-semibold text-headings">{packages.length}</span>{" "}
          packages
          {hasFilters && (
            <button
              type="button"
              onClick={clearAll}
              className="ml-3 text-xs font-semibold text-primary hover:underline"
            >
              Clear filters
            </button>
          )}
        </p>

        <div className="flex items-center gap-2">
          {/* Sort */}
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort packages"
              className="h-9 appearance-none rounded-xl border border-line bg-white pl-3 pr-8 text-[13px] font-medium text-headings shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 cursor-pointer"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={13}
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-soft"
            />
          </div>

          {/* Mobile filter toggle */}
          <button
            type="button"
            onClick={() => setSidebarOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2 text-sm font-medium text-headings shadow-sm transition hover:border-primary/40 hover:text-primary lg:hidden"
          >
            <SlidersHorizontal size={14} aria-hidden />
            Filters
            {activeFilterCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Mobile sidebar drawer ──────────────────────────────────────── */}
      {sidebarOpen && (
        <div className="mb-6 rounded-2xl border border-line bg-white p-5 shadow-sm lg:hidden">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm font-semibold text-headings">Filters</p>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close filters"
              className="text-muted-soft hover:text-headings"
            >
              <X size={16} />
            </button>
          </div>
          {sidebar}
        </div>
      )}

      {/* ── Two-column layout ──────────────────────────────────────────── */}
      <div className="grid grid-cols-12 gap-6 lg:gap-8 items-start">
        {/* Desktop sidebar */}
        <div className="hidden lg:block lg:col-span-3 rounded-2xl border border-line bg-white p-5 shadow-sm">
          {sidebar}
        </div>

        {/* Grid */}
        <div className="col-span-12 min-w-0 lg:col-span-9">
          {filtered.length > 0 ? (
            <ul className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((pkg) => (
                <li key={pkg.id}>
                  <PackageCardView
                    href={pkg.href}
                    title={pkg.title}
                    image={pkg.image}
                    imageAlt={pkg.imageAlt}
                    ribbon={pkg.ribbon}
                    rating={pkg.rating}
                    reviewCount={pkg.reviewCount}
                    duration={pkg.duration}
                    price={pkg.price}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-line py-24 text-center">
              <div className="mb-4 rounded-full bg-primary/8 p-4">
                <Search size={22} className="text-primary" aria-hidden />
              </div>
              <p className="font-display text-xl text-headings">
                No packages found
              </p>
              <p className="mt-1.5 text-sm text-muted max-w-[28ch]">
                Try adjusting your filters or search term.
              </p>
              {hasFilters && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="mt-5 rounded-full border border-primary/30 bg-primary/5 px-5 py-2 text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
